/**
 * CustomUploadFileInput Component
 *
 * A reusable file upload input component designed for Formik forms.
 * It supports file validation, dynamic icons based on file type, default files,
 * error handling, and integration with Formik's `setFieldValue`.
 *
 * Props:
 * @param {(field: string, value: File | null) => void} setFieldValue - Formik function to set the field value when a file is uploaded or removed.
 * @param {string} FieldName - Name of the Formik field to bind the uploaded file.
 * @param {string[]} allowedFormats - Optional array of allowed MIME types for file validation (e.g., ["image/png", "application/pdf"]).
 * @param {string} placeholder - Placeholder text displayed when no file is uploaded.
 * @param {string} allowedFormatsPlaceholder - Text displayed when an unsupported file type is selected.
 * @param {File | null} defaultFile - Optional default file to display on initial render.
 * @param {(changed: boolean) => void} hasChangedFacilityLicense - Optional callback triggered when the uploaded file changes.
 *
 * Behavior:
 * - Opens file selection dialog when clicking on the input area.
 * - Validates uploaded file against allowed formats and displays an error message if invalid.
 * - Displays an icon based on the uploaded file's extension (PDF, image, or default license icon).
 * - Shows file name and upload status.
 * - Allows removing uploaded file with a clickable delete icon.
 * - Integrates with Formik's ErrorMessage component for validation feedback.
 *
 * Usage:
 * ```tsx
 * <CustomUploadFileInput
 *   setFieldValue={setFieldValue}
 *   FieldName="licenseFile"
 *   allowedFormats={["image/png", "image/jpeg", "application/pdf"]}
 *   placeholder="Upload your license"
 *   allowedFormatsPlaceholder="Only PNG, JPEG, or PDF files are allowed"
 *   defaultFile={existingFile}
 *   hasChangedFacilityLicense={(changed) => console.log(changed)}
 * />
 * ```
 *
 * Notes:
 * - Supports lazy-loading icons for performance.
 * - Automatically determines file extension for icon and validation.
 * - Uses TailwindCSS for styling with conditional classes based on upload success or error.
 */

import { ErrorMessage } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomUploadFileInputProps } from "../../types/GeneralComponents";
import { BASE_LOCAL_IMAGES_DIR } from "../../utils/constants/CommonConstants";
import {
  facility_license_IMG,
  img_file_logo_IMG,
  pdf_icon,
  red_x,
} from "../../utils/constants/Images";

const CustomUploadFileInput: React.FC<CustomUploadFileInputProps> = ({
  setFieldValue,
  FieldName = "",
  allowedFormats = [],
  placeholder,
  allowedFormatsPlaceholder,
  defaultFile,
  hasChangedFacilityLicense,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  //?=====================States==============================
  const [fileName, setFileName] = useState<string | null>(
    defaultFile
      ? `Existing License.${defaultFile?.type?.split(".").pop().slice(6)}`
      : null,
  );
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(!!defaultFile);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFileExtension, setUploadedFileExtension] = useState<string>(
    defaultFile ? defaultFile?.type?.split(".").pop()?.toLowerCase() || "" : "",
  );

  useEffect(() => {
    if (defaultFile && setFieldValue) {
      setFieldValue(FieldName, defaultFile);
    }
  }, [defaultFile, setFieldValue, FieldName]);

  //?==================Click On File Input =================
  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  //?============Input On Change Handler=======================

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files
      ? event.currentTarget.files[0]
      : null;

    if (file) {
      const fileExtension = file?.name?.split(".").pop()?.toLowerCase() || "";

      if (
        setFieldValue &&
        (allowedFormats.length === 0 || allowedFormats.includes(file.type))
      ) {
        setFieldValue(FieldName, file);
        setFileName(file.name);
        setUploadSuccess(true);
        setUploadError(null);
        setUploadedFileExtension(fileExtension);

        if (hasChangedFacilityLicense) {
          hasChangedFacilityLicense(true);
        }
      } else {
        setUploadSuccess(false);
        setFileName(null);
        setUploadError(t("unsupported_file_format"));
        setUploadedFileExtension("");
      }
    } else {
      if (setFieldValue) setFieldValue(FieldName, null);
      if (hasChangedFacilityLicense) {
        hasChangedFacilityLicense(false);
      }
      setFileName(null);
      setUploadSuccess(false);
      setUploadedFileExtension("");
    }
  };

  //?==============Addressing The File Icon Based on The Extension Of the Uploaded File=================
  const getFileIcon = () => {
    if (uploadedFileExtension === "pdf") {
      return `${BASE_LOCAL_IMAGES_DIR}/${pdf_icon}`;
    } else if (
      uploadedFileExtension &&
      allowedFormats.some((mimeType) =>
        mimeType.includes(uploadedFileExtension),
      )
    ) {
      return `${BASE_LOCAL_IMAGES_DIR}/${img_file_logo_IMG}`;
    } else {
      return `${BASE_LOCAL_IMAGES_DIR}/${facility_license_IMG}`;
    }
  };

  const acceptedFileTypes = useMemo(
    () => (allowedFormats.length > 0 ? allowedFormats.join(",") : undefined),
    [allowedFormats],
  );

  return (
    <div>
      <input
        name={FieldName}
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept={acceptedFileTypes}
      />
      <div className="flex items-center justify-between">
        <div
          className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 ${
            uploadSuccess ? "" : "border border-dashed"
          } cursor-pointer ${
            uploadSuccess
              ? "border-successMain"
              : uploadError
                ? "font border-errorMain"
                : ""
          }`}
          onClick={handleFileClick}
        >
          <img src={getFileIcon()} loading="lazy" alt="uploaded file" />

          <div className="flex w-full flex-col gap-1">
            <h1
              className={`text-wrap break-all text-sm ${
                uploadSuccess
                  ? "font-bold text-textPrimaryColor"
                  : uploadError
                    ? "text-errorMain"
                    : "text-headingTextColor"
              }`}
            >
              {fileName ? fileName : placeholder}
            </h1>
            <p className="text-xs text-bodyTextColor">
              {!uploadSuccess && allowedFormatsPlaceholder}
            </p>
          </div>
        </div>
        {uploadSuccess && (
          <img
            src={`${BASE_LOCAL_IMAGES_DIR}/${red_x}`}
            alt="delete-file"
            className="cursor-pointer"
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
              if (setFieldValue) {
                setFieldValue(FieldName, "");
              }
              setUploadSuccess(false);
              setFileName(null);
              setUploadedFileExtension("");
            }}
          />
        )}
      </div>
      {uploadError && (
        <div className="mt-2 text-sm font-semibold text-errorMain">
          {uploadError}
        </div>
      )}
      <ErrorMessage
        className="mt-2 !text-sm font-semibold text-errorMain"
        name={FieldName}
        component="div"
      />
    </div>
  );
};

export default CustomUploadFileInput;
